<template>
    <div class="welcome">

        <div class="columns is-block-desktop is-block-tablet is-flex-widescreen">
            <div class="column side-panel is-6-widescreen">
                <div class="welcome-header mt-5 pt-5 content">
                    <img v-if="enrollment.course.logo_white" :src="enrollment.course.logo_white" style="height: 90px;"/>
                    <img v-else-if="enrollment.course.logo" :src="enrollment.course.logo" style="height: 90px;"/>

                    <div class="columns is-gapless is-mobile mt-6">
                        <div class="column is-narrow">
                            <i class="fad fa-fw mr-5 mt-1 is-size-3 fa-chalkboard-teacher"></i>
                        </div>
                        <div class="column">
                            <h1 class="has-text-white course-title pb-4">{{ enrollment.course.title }}</h1>

                            <render-blocks v-if="enrollment.course.welcome" :blocks="enrollment.course.welcome.content" class="course-description blocks"/>
                            <div v-else-if="enrollment.course.description" v-html="enrollment.course.description" class="course-description"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="column main-panel">

                <ui-card class="mb-6" v-if="isExpired">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-calendar-xmark mr-4 is-size-5 has-text-danger mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Course Enrollment Expired</div>
                            <div class="mb-5">This course enrollment expired on <strong>{{ formatExpireDate(enrollment.expires_on) }}</strong>. Visit <strong>Reactivate My Courses</strong> to explore your options.</div>
                            <router-link :to="{name: 'reactivation-checkout'}">
                                <b-button type="is-danger" expanded class="mb-3">
                                    <i class="fas fa-redo mr-4"></i>Reactivate My Courses
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card class="mb-6" v-if="enrollment.isMissingRequiredApps">

                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Action Required</div>
                            <div class="mb-5">This course requires a form or application to be completed before you can begin. Review the <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications & Forms</strong> to complete any requirements.</div>
                            <router-link :to="{name: 'course-applications'}">
                                <b-button type="is-danger" expanded class="mb-3">
                                    <i class="fad fa-file-certificate mr-4"></i>View Certifications & Forms
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card class="mb-6" v-if="requiresSetupApproval">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Study Plan Required</div>
                            <div class="mb-5" v-if="enrollment.setup_status === 'submitted'">Your <strong>Course Study Plan</strong> has been submitted and is awaiting approval. You will receive an email once it has been reviewed.</div>
                            <div class="mb-5" v-else>This course requires a <strong>Course Study Plan</strong> to be completed and approved before you can begin.</div>
                            <router-link :to="{name: 'course-setup'}">
                                <b-button type="is-danger" expanded class="mb-3">
                                    <i class="fad fa-clipboard-list-check mr-4"></i>{{ enrollment.setup_status === 'submitted' ? 'View Study Plan' : 'Complete Your Study Plan' }}
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card class="mb-6" v-if="enrollment.completed_at && enrollment.isIncompleteApps">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Incomplete Applications or Forms</div>
                            <div class="mb-5">Your course is complete but you have incomplete applications or forms that you may wish to complete. Review the <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications & Forms</strong>.</div>
                            <router-link :to="{name: 'course-applications'}">
                                <b-button type="is-danger" expanded class="mb-3">
                                    <i class="fad fa-file-certificate mr-4"></i>View Certifications & Forms
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <!-- Mutually exclusive with the warning card above: while a required
                     application/form is still outstanding, that card already covers
                     "complete + go to Certifications & Forms" — showing this one too
                     claimed certs/forms were ready when they weren't (found 2026-08-12
                     via a legacy-complete-but-unclaimed import). -->
                <ui-card class="mb-6" v-if="enrollment.completed_at && !enrollment.isIncompleteApps">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-circle-check mr-4 is-size-5 has-text-success mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Course Complete</div>
                            <div class="mb-5">Congratulations! You completed this course on <strong>{{ enrollment.completed_at | moment('MMMM Do, YYYY') }}</strong>. Your course is now locked, and your certificates and completion forms are available in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong>.</div>
                            <router-link :to="{name: 'course-applications'}">
                                <b-button type="is-success" expanded class="mb-3">
                                    <i class="fad fa-file-certificate mr-4"></i>View Certifications &amp; Forms
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card class="mb-6" v-else-if="applicationReadiness && enrollment.hasIncompleteEndRequiredApps">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                        <div class="column">
                            <!-- Generic wording on purpose: the required application may
                                 not be about hours/clock hours at all -->
                            <div class="mb-3 is-size-5 has-text-weight-bold">Action Required &mdash; Applications or Forms</div>
                            <div class="mb-5">You've finished your coursework. This course requires an application or form to be completed in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong> to finish the course.</div>
                            <router-link :to="{name: 'course-applications'}">
                                <b-button type="is-danger" expanded class="mb-3">
                                    <i class="fad fa-file-certificate mr-4"></i>View Certifications &amp; Forms
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card class="mb-6" v-else-if="applicationReadiness && enrollment.hasIncompleteOptionalApps">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-circle-info mr-4 is-size-5 has-text-info mt-1"></i></div>
                        <div class="column">
                            <div class="mb-3 is-size-5 has-text-weight-bold">Applications Available</div>
                            <div class="mb-5">You've finished your coursework. There are optional applications or forms available in <strong style="white-space: nowrap;"><i class="fad fa-file-certificate mr-2"></i>Certifications &amp; Forms</strong> that you may wish to complete.</div>
                            <router-link :to="{name: 'course-applications'}">
                                <b-button type="is-info" outlined expanded class="mb-3">
                                    <i class="fad fa-file-certificate mr-4"></i>View Certifications &amp; Forms
                                </b-button>
                            </router-link>
                        </div>
                    </div>
                </ui-card>

                <ui-card>
                    <div>
                        <b-button expanded size="is-large" type="is-info" :outlined="enrollment.isCourseworkComplete" @click="beginCourse" :disabled="enrollment.isMissingRequiredApps || requiresSetupApproval || isExpired">
                            <template v-if="enrollment.isCourseworkComplete">Review Course <i class="fal fa-arrow-right ml-3"></i></template>
                            <template v-else>
                                <template v-if="enrollment.last_component">Continue</template>
                                <template v-else>Begin</template>
                                Course <i class="fal fa-arrow-right ml-3"></i>
                            </template>
                        </b-button>
                    </div>

                    <div class="mb-5 mt-6 pl-4 pr-4">
                        <template v-if="maxHours > 0">
                            <div class="columns is-mobile is-vcentered is-gapless mb-4">
                                <div class="column">
                                    <b-progress :type="hoursProgressPercent >= 100 ? 'is-success' : 'is-info'" :value="hoursProgressPercent" size="is-small" class="mb-1"></b-progress>
                                    <div class="is-size-7">
                                        <strong>Hours Progress</strong> {{ totalLoggedHours }} of {{ maxHours }} hours logged ({{ hoursProgressPercent }}%)
                                    </div>
                                </div>
                                <div class="column is-narrow ml-4">
                                    <router-link :to="{name: 'study-log'}">
                                        <b-button type="is-info" size="is-small" outlined><i class="fas fa-plus mr-2"></i>Log</b-button>
                                    </router-link>
                                </div>
                            </div>
                        </template>
                        <b-progress type="is-success" :value="Math.round((enrollment.course.navigation.totalComplete/enrollment.course.navigation.totalLessons)*100)" size="is-small" class="mb-1"></b-progress>
                        <div class="is-size-7">
                            <strong>Course Progress</strong> {{ enrollment.course.navigation.totalComplete }} of {{ enrollment.course.navigation.totalLessons }} ({{ Math.round((enrollment.course.navigation.totalComplete / enrollment.course.navigation.totalLessons) * 100) }}%)
                        </div>
                        <div v-if="!enrollment.completed_at && enrollment.expires_on && !isExpired" class="is-size-7 mt-4">
                            <i class="far fa-calendar-clock mr-1"></i>This course enrollment will expire on <strong>{{ formatExpireDate(enrollment.expires_on) }}</strong>.
                        </div>
                    </div>

                    <ul class="mt-3">
                        <template v-for="(o, index) in enrollment.course.navigation.outline">
                            <template v-if="o.type === 'SECTION'">

                                <b-collapse
                                    animation="slide"
                                    :open="enrollment.course.navigation.currentSection === index"
                                    @open="enrollment.course.navigation.currentSection = index"
                                >
                                    <template #trigger="props">
                                        <li>
                                            <div class="columns is-gapless is-mobile">
                                                <div class="column">
                                                    <div class="sub-header is-size-6 mb-2">{{ o.title }}</div>
                                                    <div class="has-text-weight-normal is-text-7">{{ o.totalComplete }} of {{ o.totalLessons }} completed</div>
                                                </div>
                                                <div class="column is-narrow has-text-right">
                                                    <i class="far dropdown-icon" :class="[(props.open) ? 'fa-angle-up' : 'fa-angle-down']"></i>
                                                </div>
                                            </div>
                                        </li>
                                    </template>

                                    <!-- SECTION CHILDREN -->
                                    <template v-for="z in o.children">
                                        <li class="sub-header" v-if="z.type === 'HEADER'">{{ z.title }}</li>

                                        <!-- OVERVIEW-->
                                        <template v-if="z.type === 'OVERVIEW'">
                                            <router-link
                                                :to="courseLocked ? {} : {name: 'overview', params: {sectionId: z.id}}"
                                                custom v-slot="{ navigate, isActive }"
                                                active-class="active"
                                            >
                                                <li
                                                    @click="!courseLocked && navigate($event)"
                                                    :class="{'active': isActive, 'locked-row': courseLocked}"
                                                    :style="{'background-color': (isActive) ? colors.primary + ' !important' : 'white'}"
                                                >
                                                    <div class="columns is-gapless">
                                                        <div class="column is-narrow">
                                                            <i class="far fa-fw fa-circle-info" :style="{'color': colors.primary}"></i>
                                                        </div>
                                                        <div class="column">Overview</div>
                                                    </div>
                                                </li>
                                            </router-link>
                                        </template>

                                        <!-- COMPONENT -->
                                        <template v-else-if="z.type === 'COMPONENT'">
                                            <li
                                                v-if="z.closed"
                                                @click="!courseLocked && contentAlert()"
                                                :class="{'locked-row': courseLocked}"
                                                style="background-color: white"
                                            >
                                                <div class="columns is-gapless is-mobile">
                                                    <div class="column is-narrow">
                                                        <i class="fa-fw" :class="z.icon" :style="{'color': colors.primary}"></i>
                                                    </div>
                                                    <div class="column">{{ z.title }}</div>
                                                </div>
                                            </li>
                                            <router-link
                                                v-else
                                                :to="courseLocked ? {} : {name: 'component', params: {outlineId: z.id}}"
                                                custom v-slot="{ navigate, isActive }"
                                                active-class="active"
                                            >
                                                <li
                                                    @click="!courseLocked && navigate($event)"
                                                    :class="{'locked-row': courseLocked}"
                                                    :style="{'background-color': 'white'}"
                                                >
                                                    <div class="columns is-gapless is-mobile">
                                                        <div class="column is-narrow">
                                                            <i
                                                                class="fa-fw"
                                                                :class="z.icon"
                                                                :style="{'color': colors.primary}"
                                                            ></i>
                                                        </div>
                                                        <div class="column">
                                                            {{ z.title }}
                                                        </div>
                                                        <div class="column has-text-right is-narrow" v-if="!courseLocked && (isActive || (enrollment.last_component && z.current))">
                                                            <b-tag type="is-info"><i class="fas fa-location-dot has-text-white is-size-7 mr-2 mb-1"></i>Current Lesson</b-tag>
                                                        </div>
                                                    </div>
                                                </li>
                                            </router-link>
                                        </template>
                                    </template>
                                </b-collapse>
                            </template>
                        </template>
                    </ul>

                </ui-card>
            </div>
        </div>
    </div>
</template>

<script>

    import {mapGetters} from "vuex";
    import moment from "moment";

    import UiCard from "../../components/UiCard.vue";
    import RenderBlocks from "../../components/course/RenderBlocks.vue";

    export default {
        components: {UiCard, RenderBlocks},
        props: ['enrollmentId'],

        computed: {
            ...mapGetters(['colors', 'enrollment', 'currentUser', 'applicationReadiness']),
            isSelfDirected() {
                return this.enrollment &&
                       this.enrollment.course &&
                       this.enrollment.course.type === 'self_directed';
            },
            requiresSetupApproval() {
                return this.isSelfDirected && this.enrollment.setup_status !== 'approved';
            },
            // Same source as StudyLog.vue: the linked config's units
            maxHours() {
                if (!this.enrollment?.configs || this.enrollment.configs.length === 0) return 0;
                return this.enrollment.configs[0]?.max_hours || 0;
            },
            totalLoggedHours() {
                return this.enrollment?.total_logged_hours || 0;
            },
            hoursProgressPercent() {
                if (!this.maxHours) return 0;
                return Math.min(Math.round((this.totalLoggedHours / this.maxHours) * 100), 100);
            },
            isExpired() {
                if (!this.enrollment?.expires_on) return false;
                return moment.utc(this.enrollment.expires_on).isBefore(moment(), 'day');
            },
            courseLocked() {
                return this.enrollment.isMissingRequiredApps || this.requiresSetupApproval || this.isExpired;
            }
        },
        methods: {
            // expires_on is a DATE column — format in UTC so the day never
            // shifts backward in western timezones
            formatExpireDate(date) {
                return moment.utc(date).format('MMMM Do, YYYY');
            },
            beginCourse: function() {
                if (!this.sectionId && !this.outlineId && this.enrollment.last_component) {
                    console.log('Going to last component');
                    this.$router.push({name: 'component', params: {outlineId: this.enrollment.last_component}});
                } else {
                    if (this.enrollment.course.navigation.outline[0].children[0].type === 'OVERVIEW') {
                        console.log('Going to start, overview', this.enrollment.course.navigation.outline[0].children[0]);
                        this.$router.push({name: 'overview', params: {sectionId: this.enrollment.course.navigation.outline[0].children[0].id}});
                    } else {
                        console.log('Going to start, component', this.enrollment.course.navigation.outline[0].children[0]);
                        this.$router.push({name: 'component', params: {outlineId: this.enrollment.course.navigation.outline[0].children[0].id}});
                    }
                }
            },
            contentAlert: function() {
                window.alert('This content is not available until you complete previous content.');
            }
        }
    };
</script>

<style scoped lang="scss">

    .welcome {

        .side-panel {
            position: relative;
            transition: width 100ms;
            color: white;

            .welcome-header {
                margin-right: 4rem;

                @media (max-width: 768px) {
                    margin: 0;
                }
            }

            .course-title {
                //@media (max-width: 768px) {
                //    margin: 0 !important;
                //    padding: 0 !important;
                //}
            }

            .course-description {

                &.blocks {
                    margin-left: 2rem;
                }

            }
        }

        .main-panel {
            position: relative;

            margin: 4.5rem 0;
            padding: 2rem;

            ul {
                margin-top: 1rem;
                box-shadow: 2px 2px 20px 0 rgba(0, 0, 0, 0.1);
                border-radius: 0.5rem;

                .section-collapse {
                    //border-radius: 0.5rem;
                    border-bottom: 1px solid #f4f4f4;

                    &:last-child {
                        border-radius: 0;
                    }
                }

                li {
                    border-top: 1px solid #f4f4f4;
                    font-size: 0.85rem;
                    line-height: 1.1rem;
                    padding: 0.9rem 2rem 0.7rem 1rem;
                    color: #4a4a4a;
                    transition: all 200ms;
                    font-weight: bold;
                    cursor: pointer;

                    &.locked-row {
                        cursor: default;

                        &:hover {
                            background-color: white !important;
                        }
                    }

                    &.sub-header {
                        font-size: 1rem;
                        padding-right: 0.8rem;

                        &:last-child {
                            //border-radius: 0;
                            //border-bottom: 2px solid #e1e1e1;
                        }

                    }

                    &.active {
                        background-color: #336699 !important;

                        color: white;

                        .meta, i {
                            color: white !important;
                        }
                    }

                    i {
                        color: #3c6691;
                        font-size: 1.2rem;
                        vertical-align: middle;
                        transition: all 200ms;
                        margin-right: 0.9rem
                    }

                    &:last-child {
                        border-radius: 0.5rem;
                    }
                }
            }

            @media (max-width: 768px) {
                padding: 0;
            }
        }
    }
</style>
